import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Building2, Users, Lock, Unlock, ChevronRight, UserCog, LogIn, KeyRound, Trash2, MoreHorizontal, Download, CloudUpload, Trash, DollarSign, Sparkles, Zap, Plus, Mail, AlertCircle, Paperclip, X, Handshake, History, Loader2, Phone } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { DomainStatusPanel } from "@/components/domain-status-panel";
import { CreditsDialog } from "@/components/platform/credits-dialog";

interface Workspace {
  id: string;
  slug: string;
  name: string;
  tier: string;
  isActive: boolean;
  isLocked: boolean;
  userCount: number;
  createdAt: string;
  // White-label custom domain — used to decide whether to render the live
  // DNS status indicator on this card. The platform list endpoint returns
  // the full workspace row, so this field is already populated server-side.
  customDomain: string | null;
  sponsorHubEnabled: boolean | null;
}

interface PlatformTier {
  id: string;
  name: string;
  audience: "individual" | "team_enterprise" | null;
}

type AudienceFilter = "all" | "individual" | "team_enterprise";

interface WorkspaceUser {
  userId: string;
  email: string;
  name: string;
  role: string;
}

const tierColors: Record<string, string> = {
  free: "bg-slate-500",
  pro: "bg-blue-500",
  business: "bg-purple-500",
  enterprise: "bg-amber-500",
};

export function WorkspacesContent() {
  const { toast } = useToast();
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [manageUsersDialogOpen, setManageUsersDialogOpen] = useState(false);
  const [manageUsersWorkspace, setManageUsersWorkspace] = useState<Workspace | null>(null);
  const [recoverWorkspace, setRecoverWorkspace] = useState<Workspace | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<WorkspaceUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<WorkspaceUser | null>(null);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingWorkspace, setBillingWorkspace] = useState<Workspace | null>(null);
  const [overrideMonthly, setOverrideMonthly] = useState<string>("");
  const [overrideYearly, setOverrideYearly] = useState<string>("");
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>("all");

  // Credits management dialog
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditsWorkspace, setCreditsWorkspace] = useState<Workspace | null>(null);
  const [creditsTab, setCreditsTab] = useState<"ai" | "lead">("ai");

  // Phone provisioning dialog
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneWorkspace, setPhoneWorkspace] = useState<Workspace | null>(null);
  const [phoneAreaCode, setPhoneAreaCode] = useState("");

  // Create Workspace dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createTier, setCreateTier] = useState("enterprise");
  const [createSeedData, setCreateSeedData] = useState(true);

  // Welcome Email dialog (opens automatically after workspace creation)
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeInfo, setWelcomeInfo] = useState<{ name: string; tier: string; ownerEmail: string } | null>(null);
  const [welcomeTemplateId, setWelcomeTemplateId] = useState<string>("");
  const [welcomeSubject, setWelcomeSubject] = useState("");
  const [welcomeBody, setWelcomeBody] = useState("");
  const [welcomeCc, setWelcomeCc] = useState("");
  const [welcomeFiles, setWelcomeFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Platform email templates — fetched when welcome dialog opens
  const { data: platformTemplates } = useQuery<Array<{ id: string; name: string; subject: string; body: string }>>({
    queryKey: ["/api/platform/communications/templates"],
    enabled: welcomeOpen,
  });

  // Platform admin Gmail status — fetched when welcome dialog opens
  const { data: gmailStatus } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/platform/gmail/status"],
    enabled: welcomeOpen,
  });

  const { data: workspaces, isLoading } = useQuery<Workspace[]>({
    queryKey: ["/api/platform/workspaces"],
  });

  const { data: platformTiers } = useQuery<PlatformTier[]>({
    queryKey: ["/api/platform/tiers"],
  });

  const tierAudienceByName = (() => {
    const m = new Map<string, "individual" | "team_enterprise" | null>();
    (platformTiers ?? []).forEach((t) => m.set(t.name, t.audience));
    return m;
  })();

  const filteredWorkspaces = (workspaces ?? []).filter((w) => {
    if (audienceFilter === "all") return true;
    const aud = tierAudienceByName.get(w.tier);
    return aud === audienceFilter;
  });

  const { data: workspaceUsers } = useQuery<WorkspaceUser[]>({
    queryKey: ["/api/platform/workspaces", manageUsersWorkspace?.id, "users"],
    enabled: !!manageUsersWorkspace,
  });

  const { data: workspaceAiProfiles } = useQuery<any[]>({
    queryKey: ["/api/platform/workspaces", manageUsersWorkspace?.id, "ai-user-profiles"],
    enabled: !!manageUsersWorkspace,
  });

  // ── Deleted-client recovery (super-admin undo) ──────────────────────────
  const { data: deletedClients, isLoading: deletedClientsLoading } = useQuery<any[]>({
    queryKey: ["/api/platform/workspaces", recoverWorkspace?.id, "deleted-clients"],
    enabled: !!recoverWorkspace,
  });

  const restoreClientMutation = useMutation({
    mutationFn: async ({ workspaceId, clientId }: { workspaceId: string; clientId: string }) =>
      apiRequest("POST", `/api/platform/workspaces/${workspaceId}/clients/${clientId}/restore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces", recoverWorkspace?.id, "deleted-clients"] });
      toast({ title: "Client restored", description: "The client and its invoices are back in the workspace." });
    },
    onError: (error: any) => {
      toast({ title: "Restore failed", description: error?.message || "Could not restore client.", variant: "destructive" });
    },
  });

  const updateTierMutation = useMutation({
    mutationFn: async ({ id, tier }: { id: string; tier: string }) => {
      return apiRequest("PATCH", `/api/platform/workspaces/${id}/tier`, { tier });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Tier updated successfully" });
    },
  });

  const toggleLockMutation = useMutation({
    mutationFn: async ({ id, isLocked }: { id: string; isLocked: boolean }) => {
      return apiRequest("PATCH", `/api/platform/workspaces/${id}/lock`, { isLocked });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Workspace lock status updated" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/platform/workspaces/${id}/status`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Workspace status updated" });
    },
  });

  const toggleSponsorHubMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      return apiRequest("PATCH", `/api/platform/workspaces/${id}/sponsor-hub`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Sponsor Hub updated" });
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: async ({ workspaceId, userId }: { workspaceId: string; userId: string }) => {
      return apiRequest("POST", "/api/platform/impersonate", { workspaceId, userId });
    },
    onSuccess: () => {
      toast({ title: "Impersonation started" });
      window.location.href = "/";
    },
  });

  const [deleteWorkspaceOpen, setDeleteWorkspaceOpen] = useState(false);
  const [deleteWorkspaceTarget, setDeleteWorkspaceTarget] = useState<Workspace | null>(null);
  const [deleteMode, setDeleteMode] = useState<"soft" | "hard" | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const exportWorkspaceMutation = useMutation({
    mutationFn: async ({ workspaceId, slug }: { workspaceId: string; slug: string }) => {
      const res = await fetch(`/api/platform/workspaces/${workspaceId}/export`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.text()) || "Failed to export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().split("T")[0];
      a.download = `${slug}_export_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast({ title: "Workspace exported" }),
    onError: (e: any) => toast({ title: "Export failed", description: e?.message, variant: "destructive" }),
  });

  const backupWorkspaceMutation = useMutation({
    mutationFn: async ({ workspaceId }: { workspaceId: string }) => {
      const res = await apiRequest("POST", `/api/platform/workspaces/${workspaceId}/backup`, {});
      return res as unknown as { folderUrl: string; rowCount: number };
    },
    onSuccess: (data: any) => {
      toast({
        title: "Backup complete",
        description: `${data.rowCount} rows uploaded to Drive`,
      });
      if (data.folderUrl) window.open(data.folderUrl, "_blank");
    },
    onError: (e: any) => toast({ title: "Backup failed", description: e?.message, variant: "destructive" }),
  });

  const deleteWorkspaceMutation = useMutation({
    mutationFn: async ({
      workspaceId,
      mode,
      workspaceName,
    }: {
      workspaceId: string;
      mode: "soft" | "hard";
      workspaceName?: string;
    }) => {
      // Task #661 — Hard-delete also schedules per-workspace KEK destruction.
      // Send the explicit acknowledgment + confirm phrase the server now
      // requires so an accidental click can't quietly destroy the
      // encryption key. Soft-delete is recoverable and stays as-is.
      const body =
        mode === "hard"
          ? {
              mode: "hard",
              confirmKekDestruction: true,
              confirmPhrase: `DESTROY ${workspaceName ?? ""}`,
            }
          : { mode };
      return apiRequest("DELETE", `/api/platform/workspaces/${workspaceId}?mode=${mode}`, body);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/approval-requests"] });
      toast(
        vars.mode === "hard"
          ? {
              title: "Workspace permanently deleted",
              description: "The workspace was hard-deleted immediately.",
            }
          : { title: "Workspace soft-deleted" },
      );
      setDeleteWorkspaceOpen(false);
      setDeleteWorkspaceTarget(null);
      setDeleteMode(null);
      setDeleteConfirmText("");
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e?.message, variant: "destructive" }),
  });

  // ── Merge-tag resolver for welcome email templates ──────────────────────
  function resolveMergeTagsForWelcome(
    text: string,
    vars: { workspace_name: string; owner_email: string; tier: string; login_url: string; sender_name?: string },
  ): string {
    return text
      .replace(/\{\{workspace_name\}\}/g, vars.workspace_name)
      .replace(/\{\{owner_email\}\}/g, vars.owner_email)
      .replace(/\{\{tier\}\}/g, vars.tier)
      .replace(/\{\{login_url\}\}/g, vars.login_url)
      .replace(/\{\{sender_name\}\}/g, vars.sender_name ?? "");
  }

  const sendWelcomeEmailMutation = useMutation({
    mutationFn: async (payload: {
      templateId?: string;
      to: string;
      cc: string[];
      subject: string;
      body: string;
      attachmentDataUrls: string[];
    }) => apiRequest("POST", "/api/platform/welcome-email", payload),
    onSuccess: () => {
      toast({ title: `Welcome email sent to ${welcomeInfo?.ownerEmail}` });
      closeWelcomeDialog();
    },
    onError: (e: any) => toast({ title: "Failed to send welcome email", description: e?.message, variant: "destructive" }),
  });

  function closeWelcomeDialog() {
    setWelcomeOpen(false);
    setWelcomeInfo(null);
    setWelcomeTemplateId("");
    setWelcomeSubject("");
    setWelcomeBody("");
    setWelcomeCc("");
    setWelcomeFiles([]);
  }

  async function handleSendWelcomeEmail() {
    if (!welcomeInfo) return;
    const ccList = welcomeCc
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Convert File objects to base64 data URLs
    const attachmentDataUrls: string[] = [];
    for (const file of welcomeFiles) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      attachmentDataUrls.push(dataUrl);
    }

    sendWelcomeEmailMutation.mutate({
      templateId: welcomeTemplateId || undefined,
      to: welcomeInfo.ownerEmail,
      cc: ccList,
      subject: welcomeSubject,
      body: welcomeBody,
      attachmentDataUrls,
    });
  }

  const createWorkspaceMutation = useMutation({
    mutationFn: async (body: { name: string; slug: string; ownerEmail: string; tier: string; seedSampleData: boolean }) =>
      apiRequest("POST", "/api/platform/workspaces", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Workspace created", description: `${createName} is ready.` });
      // Capture form values before resetting, then open welcome email dialog
      const info = { name: createName, tier: createTier, ownerEmail: createEmail };
      setCreateOpen(false);
      setCreateName("");
      setCreateSlug("");
      setCreateEmail("");
      setCreateTier("enterprise");
      setCreateSeedData(true);
      // Open welcome email dialog with pre-filled context
      setWelcomeInfo(info);
      setWelcomeSubject(`Welcome to Clarik — ${info.name}`);
      setWelcomeBody("");
      setWelcomeTemplateId("");
      setWelcomeOpen(true);
    },
    onError: (e: any) => toast({ title: "Failed to create workspace", description: e?.message, variant: "destructive" }),
  });

  const enterWorkspaceMutation = useMutation({
    mutationFn: async ({ workspaceId, slug }: { workspaceId: string; slug: string }) => {
      await apiRequest("POST", "/api/platform/enter-workspace", { workspaceId });
      return { slug };
    },
    onSuccess: (data) => {
      toast({ title: "Entering workspace" });
      // Use the slug from the locally-known workspace (deterministic) instead of trusting backend response
      window.location.href = data.slug ? `/w/${data.slug}/` : "/";
    },
    onError: (error: any) => {
      toast({ title: "Failed to enter workspace", description: error?.message, variant: "destructive" });
    },
  });


  const deleteUserMutation = useMutation({
    mutationFn: async ({ workspaceId, userId }: { workspaceId: string; userId: string }) => {
      return apiRequest("DELETE", `/api/platform/workspaces/${workspaceId}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces", manageUsersWorkspace?.id, "users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "User removed from workspace" });
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      toast({ title: "Failed to remove user", description: error.message, variant: "destructive" });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ workspaceId, userId, newPassword }: { workspaceId: string; userId: string; newPassword: string }) => {
      return apiRequest("POST", `/api/platform/workspaces/${workspaceId}/users/${userId}/reset-password`, { newPassword });
    },
    onSuccess: () => {
      toast({ title: "Password reset successfully" });
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword("");
    },
    onError: (error: any) => {
      toast({ title: "Failed to reset password", description: error.message, variant: "destructive" });
    },
  });

  const handleManageUsers = (workspace: Workspace) => {
    setManageUsersWorkspace(workspace);
    setManageUsersDialogOpen(true);
  };

  const handleResetPassword = (user: WorkspaceUser) => {
    setResetPasswordUser(user);
    setResetPasswordDialogOpen(true);
  };

  const handleDeleteUser = (user: WorkspaceUser) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmResetPassword = () => {
    if (manageUsersWorkspace && resetPasswordUser && newPassword) {
      resetPasswordMutation.mutate({
        workspaceId: manageUsersWorkspace.id,
        userId: resetPasswordUser.userId,
        newPassword,
      });
    }
  };

  const confirmDeleteUser = () => {
    if (manageUsersWorkspace && userToDelete) {
      deleteUserMutation.mutate({
        workspaceId: manageUsersWorkspace.id,
        userId: userToDelete.userId,
      });
    }
  };

  return (
    <>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Workspaces</h1>
            <p className="text-muted-foreground">
              Manage all tenant workspaces and their settings
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground" htmlFor="audience-filter">
              Audience
            </Label>
            <Select
              value={audienceFilter}
              onValueChange={(v) => setAudienceFilter(v as AudienceFilter)}
            >
              <SelectTrigger
                id="audience-filter"
                className="w-48"
                data-testid="select-workspaces-audience-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="team_enterprise">Team & Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="button-create-workspace"
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredWorkspaces.length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center" data-testid="text-empty-workspaces">
                No workspaces match this audience filter.
              </div>
            )}
            {filteredWorkspaces.map((workspace) => (
              <Card 
                key={workspace.id}
                className={`${workspace.isLocked ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''}`}
                data-testid={`workspace-card-${workspace.slug}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <CardTitle className="text-lg">{workspace.name}</CardTitle>
                        <CardDescription>{workspace.slug}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {workspace.isLocked && (
                        <Badge variant="destructive">
                          <Lock className="h-3 w-3 mr-1" />
                          Locked
                        </Badge>
                      )}
                      {!workspace.isActive && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      <Badge className={tierColors[workspace.tier]}>
                        {workspace.tier}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {workspace.userCount} users
                      </div>
                      <div>
                        Created: {new Date(workspace.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select
                        value={workspace.tier}
                        onValueChange={(tier) => updateTierMutation.mutate({ id: workspace.id, tier })}
                      >
                        <SelectTrigger className="w-32" data-testid={`select-tier-${workspace.slug}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleLockMutation.mutate({ 
                          id: workspace.id, 
                          isLocked: !workspace.isLocked 
                        })}
                        data-testid={`button-lock-${workspace.slug}`}
                      >
                        {workspace.isLocked ? (
                          <><Unlock className="h-4 w-4 mr-1" /> Unlock</>
                        ) : (
                          <><Lock className="h-4 w-4 mr-1" /> Lock</>
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleManageUsers(workspace)}
                        data-testid={`button-manage-users-${workspace.slug}`}
                      >
                        <UserCog className="h-4 w-4 mr-1" />
                        Manage Users
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBillingWorkspace(workspace);
                          setBillingDialogOpen(true);
                        }}
                        data-testid={`button-billing-${workspace.slug}`}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Billing
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Workspace actions"
                            data-testid={`button-workspace-actions-${workspace.slug}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onClick={() => {
                              setCreditsWorkspace(workspace);
                              setCreditsTab("ai");
                              setCreditsOpen(true);
                            }}
                            data-testid={`menu-ai-credits-${workspace.slug}`}
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            AI Credits
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setCreditsWorkspace(workspace);
                              setCreditsTab("lead");
                              setCreditsOpen(true);
                            }}
                            data-testid={`menu-lead-credits-${workspace.slug}`}
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            Lead Credits
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() =>
                              exportWorkspaceMutation.mutate({ workspaceId: workspace.id, slug: workspace.slug })
                            }
                            disabled={exportWorkspaceMutation.isPending}
                            data-testid={`menu-export-workspace-${workspace.slug}`}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {exportWorkspaceMutation.isPending ? "Exporting..." : "Export to .xlsx"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => backupWorkspaceMutation.mutate({ workspaceId: workspace.id })}
                            disabled={backupWorkspaceMutation.isPending}
                            data-testid={`menu-backup-workspace-${workspace.slug}`}
                          >
                            <CloudUpload className="h-4 w-4 mr-2" />
                            {backupWorkspaceMutation.isPending ? "Backing up..." : "Backup to Drive"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setRecoverWorkspace(workspace)}
                            data-testid={`menu-recover-clients-${workspace.slug}`}
                          >
                            <History className="h-4 w-4 mr-2" />
                            Recover deleted clients
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setDeleteWorkspaceTarget(workspace);
                              setDeleteMode(null);
                              setDeleteConfirmText("");
                              setDeleteWorkspaceOpen(true);
                            }}
                            className="text-destructive focus:text-destructive"
                            data-testid={`menu-delete-workspace-${workspace.slug}`}
                          >
                            <Trash className="h-4 w-4 mr-2" />
                            Delete workspace
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => enterWorkspaceMutation.mutate({ workspaceId: workspace.id, slug: workspace.slug })}
                        disabled={enterWorkspaceMutation.isPending}
                        title="Enter workspace as super admin"
                        data-testid={`button-enter-workspace-${workspace.slug}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/*
                    Live DNS status for this workspace's white-label custom
                    domain. Only rendered when a custom domain is configured —
                    otherwise the panel would just say "no domain configured"
                    on every card. Reuses the exact same indicator copy as
                    the workspace-side white-label settings panel so support
                    sees the same five-state diagnosis without impersonating.
                  */}
                  {workspace.customDomain && (
                    <div
                      className="mt-3 pt-3 border-t border-border"
                      data-testid={`section-domain-status-${workspace.slug}`}
                    >
                      <div className="text-xs text-muted-foreground mb-1.5">
                        Custom domain DNS · <span className="font-mono">{workspace.customDomain}</span>
                      </div>
                      <DomainStatusPanel
                        queryUrl={`/api/platform/workspaces/${workspace.id}/domain-status`}
                        queryKey={["/api/platform/workspaces", workspace.id, "domain-status"]}
                        // Platform-admin surface is read-only across other
                        // workspaces — never auto-fire verify on someone
                        // else's behalf. The workspace admin uses the
                        // settings panel for that.
                        verifyUrl={null}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── Welcome Email dialog (auto-opens after workspace creation) ── */}
        <Dialog open={welcomeOpen} onOpenChange={(open) => { if (!open) closeWelcomeDialog(); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Send Welcome Email
              </DialogTitle>
              <DialogDescription>
                Send a welcome message to {welcomeInfo?.ownerEmail} for workspace <strong>{welcomeInfo?.name}</strong>.
              </DialogDescription>
            </DialogHeader>

            {/* Gmail warning */}
            {gmailStatus && !gmailStatus.connected && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Connect Gmail in <strong>Platform Settings → Email</strong> to send from your account. This email will be sent via the system mailer instead.
                </span>
              </div>
            )}

            <div className="space-y-4 py-1">
              {/* Template picker */}
              <div className="space-y-1.5">
                <Label htmlFor="welcome-template">Template <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Select
                  value={welcomeTemplateId}
                  onValueChange={(id) => {
                    setWelcomeTemplateId(id);
                    const tpl = platformTemplates?.find((t) => t.id === id);
                    if (tpl && welcomeInfo) {
                      const vars = {
                        workspace_name: welcomeInfo.name,
                        owner_email: welcomeInfo.ownerEmail,
                        tier: welcomeInfo.tier,
                        login_url: "https://clarik.io/",
                      };
                      setWelcomeSubject(resolveMergeTagsForWelcome(tpl.subject || "", vars));
                      setWelcomeBody(resolveMergeTagsForWelcome(tpl.body || "", vars));
                    }
                  }}
                >
                  <SelectTrigger id="welcome-template" data-testid="select-welcome-template">
                    <SelectValue placeholder="Choose a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(platformTemplates ?? []).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No templates yet</div>
                    )}
                    {(platformTemplates ?? []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* To (read-only) */}
              <div className="space-y-1.5">
                <Label>To</Label>
                <Input
                  value={welcomeInfo?.ownerEmail ?? ""}
                  readOnly
                  className="bg-muted/50 cursor-default"
                  data-testid="input-welcome-to"
                />
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <Label htmlFor="welcome-subject">Subject</Label>
                <Input
                  id="welcome-subject"
                  value={welcomeSubject}
                  onChange={(e) => setWelcomeSubject(e.target.value)}
                  placeholder="Welcome to Clarik!"
                  data-testid="input-welcome-subject"
                />
              </div>

              {/* Body */}
              <div className="space-y-1.5">
                <Label htmlFor="welcome-body">Message</Label>
                <Textarea
                  id="welcome-body"
                  value={welcomeBody}
                  onChange={(e: any) => setWelcomeBody(e.target.value)}
                  placeholder="Write your welcome message here…"
                  className="min-h-[140px] font-mono text-sm"
                  data-testid="textarea-welcome-body"
                />
                <p className="text-xs text-muted-foreground">
                  Supported merge tags: <code>{"{{workspace_name}}"}</code>, <code>{"{{owner_email}}"}</code>, <code>{"{{tier}}"}</code>, <code>{"{{login_url}}"}</code>
                </p>
              </div>

              {/* CC */}
              <div className="space-y-1.5">
                <Label htmlFor="welcome-cc">CC <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
                <Input
                  id="welcome-cc"
                  value={welcomeCc}
                  onChange={(e) => setWelcomeCc(e.target.value)}
                  placeholder="cc@example.com, another@example.com"
                  data-testid="input-welcome-cc"
                />
              </div>

              {/* Attachments */}
              <div className="space-y-1.5">
                <Label>Attachments</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="button-welcome-attach"
                  >
                    <Paperclip className="h-3.5 w-3.5 mr-1" />
                    Attach files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      setWelcomeFiles((prev) => [...prev, ...files]);
                      e.target.value = "";
                    }}
                    data-testid="input-welcome-file"
                  />
                  {welcomeFiles.map((f, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1 text-xs bg-muted rounded-full px-2 py-0.5"
                      data-testid={`chip-attachment-${i}`}
                    >
                      {f.name}
                      <button
                        type="button"
                        onClick={() => setWelcomeFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="text-muted-foreground hover:text-foreground ml-0.5"
                        data-testid={`button-remove-attachment-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={closeWelcomeDialog}
                disabled={sendWelcomeEmailMutation.isPending}
                data-testid="button-welcome-skip"
              >
                Skip
              </Button>
              <Button
                onClick={handleSendWelcomeEmail}
                disabled={
                  sendWelcomeEmailMutation.isPending ||
                  !welcomeSubject.trim() ||
                  !welcomeBody.trim() ||
                  (gmailStatus !== undefined && !gmailStatus.connected)
                }
                data-testid="button-welcome-send"
              >
                {sendWelcomeEmailMutation.isPending ? (
                  <>
                    <span className="animate-spin mr-2 inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-1" />
                    Send
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Create Workspace dialog ── */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Workspace</DialogTitle>
              <DialogDescription>
                Provision a new workspace and assign an owner by email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="cw-name">Workspace name</Label>
                <Input
                  id="cw-name"
                  placeholder="Acme Entertainment"
                  value={createName}
                  onChange={(e) => {
                    setCreateName(e.target.value);
                    if (!createSlug || createSlug === createName.toLowerCase().replace(/[^a-z0-9]+/g, "-")) {
                      setCreateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
                    }
                  }}
                  data-testid="input-create-workspace-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cw-slug">Slug <span className="text-muted-foreground text-xs">(URL-safe, unique)</span></Label>
                <Input
                  id="cw-slug"
                  placeholder="acme-entertainment"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  data-testid="input-create-workspace-slug"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cw-email">Owner email</Label>
                <Input
                  id="cw-email"
                  type="email"
                  placeholder="theonlannister.676730@gmail.com"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  data-testid="input-create-workspace-email"
                />
                <p className="text-xs text-muted-foreground">
                  If this person doesn't have an account yet, one will be created. They'll be linked on first sign-in.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cw-tier">Tier</Label>
                <Select value={createTier} onValueChange={setCreateTier}>
                  <SelectTrigger id="cw-tier" data-testid="select-create-workspace-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lite">Lite</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="cw-seed"
                  type="checkbox"
                  checked={createSeedData}
                  onChange={(e) => setCreateSeedData(e.target.checked)}
                  className="rounded border-border"
                  data-testid="checkbox-create-workspace-seed"
                />
                <Label htmlFor="cw-seed" className="font-normal cursor-pointer">
                  Seed sample data (2 leads, 1 client, 1 invoice)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="button-create-workspace-cancel">
                Cancel
              </Button>
              <Button
                onClick={() =>
                  createWorkspaceMutation.mutate({
                    name: createName,
                    slug: createSlug,
                    ownerEmail: createEmail,
                    tier: createTier,
                    seedSampleData: createSeedData,
                  })
                }
                disabled={createWorkspaceMutation.isPending || !createName || !createSlug || !createEmail}
                data-testid="button-create-workspace-submit"
              >
                {createWorkspaceMutation.isPending ? "Creating…" : "Create workspace"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={manageUsersDialogOpen} onOpenChange={setManageUsersDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Manage Users</DialogTitle>
              <DialogDescription>
                Manage users in {manageUsersWorkspace?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-auto">
              {workspaceUsers?.map((user) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  data-testid={`user-row-${user.userId}`}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                    <Badge variant="secondary">{user.role}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (manageUsersWorkspace) {
                          impersonateMutation.mutate({
                            workspaceId: manageUsersWorkspace.id,
                            userId: user.userId,
                          });
                        }
                      }}
                      disabled={impersonateMutation.isPending}
                      data-testid={`button-login-as-${user.userId}`}
                    >
                      <LogIn className="h-4 w-4 mr-1" />
                      Login As
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`button-user-actions-${user.userId}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleResetPassword(user)}
                          data-testid={`menu-reset-password-${user.userId}`}
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteUser(user)}
                          className="text-destructive"
                          data-testid={`menu-delete-user-${user.userId}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove from Workspace
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
              {workspaceUsers?.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No users found in this workspace
                </p>
              )}
            </div>

            {/* AI Settings — read-only per-user tone profiles */}
            {workspaceAiProfiles && workspaceAiProfiles.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  AI Settings (read-only)
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="pb-1 pr-3 font-medium">User ID</th>
                        <th className="pb-1 pr-3 font-medium">Coach</th>
                        <th className="pb-1 pr-3 font-medium">Cadence</th>
                        <th className="pb-1 pr-3 font-medium">Archetype</th>
                        <th className="pb-1 pr-3 font-medium">Intensity</th>
                        <th className="pb-1 pr-3 font-medium">Samples</th>
                        <th className="pb-1 font-medium">Formality</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {workspaceAiProfiles.map((p: any) => (
                        <tr key={p.userId} data-testid={`row-ai-profile-${p.userId}`}>
                          <td className="py-1 pr-3 font-mono text-[10px] text-muted-foreground">{p.userId.slice(0, 8)}…</td>
                          <td className="py-1 pr-3">
                            {p.coachEnabled
                              ? <span className="text-green-600 font-medium">On</span>
                              : <span className="text-muted-foreground">Off</span>
                            }
                          </td>
                          <td className="py-1 pr-3 text-muted-foreground">{p.initiationFrequency ?? "—"}</td>
                          <td className="py-1 pr-3 text-muted-foreground capitalize">{p.personaArchetype ?? "—"}</td>
                          <td className="py-1 pr-3 text-muted-foreground capitalize">{p.intensity ?? "—"}</td>
                          <td className="py-1 pr-3">{p.sampleCount ?? 0}</td>
                          <td className="py-1">{p.formalityScore !== null && p.formalityScore !== undefined ? Number(p.formalityScore).toFixed(2) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ── Recover deleted clients (super-admin undo) ── */}
        <Dialog open={!!recoverWorkspace} onOpenChange={(open) => { if (!open) setRecoverWorkspace(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Recover deleted clients</DialogTitle>
              <DialogDescription>
                Soft-deleted clients in {recoverWorkspace?.name}. Restoring re-attaches the client along with any invoices that still point to it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-auto">
              {deletedClientsLoading && (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
                </div>
              )}
              {!deletedClientsLoading && deletedClients?.map((c: any) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  data-testid={`deleted-client-row-${c.id}`}
                >
                  <div>
                    <div className="font-medium">
                      {c.accountName || c.contactName || "(no name)"}
                      {c.recordId && <span className="ml-2 text-xs font-mono text-muted-foreground">{c.recordId}</span>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {c.contactName ? `${c.contactName} · ` : ""}{c.emailAddress || "no email"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Event: {c.dateOfEvent || "—"} · Deleted: {c.deletedAt ? new Date(c.deletedAt).toLocaleString() : "—"}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => recoverWorkspace && restoreClientMutation.mutate({ workspaceId: recoverWorkspace.id, clientId: c.id })}
                    disabled={restoreClientMutation.isPending}
                    data-testid={`button-restore-client-${c.id}`}
                  >
                    <History className="h-4 w-4 mr-1" /> Restore
                  </Button>
                </div>
              ))}
              {!deletedClientsLoading && deletedClients?.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No deleted clients in this workspace.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <BillingDialog
          open={billingDialogOpen}
          onOpenChange={(open) => {
            setBillingDialogOpen(open);
            if (!open) {
              setBillingWorkspace(null);
              setOverrideMonthly("");
              setOverrideYearly("");
            }
          }}
          workspace={billingWorkspace}
          overrideMonthly={overrideMonthly}
          overrideYearly={overrideYearly}
          setOverrideMonthly={setOverrideMonthly}
          setOverrideYearly={setOverrideYearly}
        />

        <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => {
          setResetPasswordDialogOpen(open);
          if (!open) setNewPassword("");
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {resetPasswordUser?.name || resetPasswordUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="input-new-password"
                />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Password requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li className={newPassword.length >= 8 ? "text-green-600" : ""}>At least 8 characters</li>
                    <li className={/[A-Z]/.test(newPassword) ? "text-green-600" : ""}>At least one uppercase letter</li>
                    <li className={/[a-z]/.test(newPassword) ? "text-green-600" : ""}>At least one lowercase letter</li>
                    <li className={/[0-9]/.test(newPassword) ? "text-green-600" : ""}>At least one number</li>
                  </ul>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setResetPasswordDialogOpen(false);
                  setNewPassword("");
                }}
                data-testid="button-cancel-reset"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmResetPassword}
                disabled={
                  newPassword.length < 8 || 
                  !/[A-Z]/.test(newPassword) ||
                  !/[a-z]/.test(newPassword) ||
                  !/[0-9]/.test(newPassword) ||
                  resetPasswordMutation.isPending
                }
                data-testid="button-confirm-reset"
              >
                {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove User from Workspace</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {userToDelete?.name || userToDelete?.email} from {manageUsersWorkspace?.name}? 
                This will revoke their access to this workspace.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteUser}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="button-confirm-delete"
              >
                {deleteUserMutation.isPending ? "Removing..." : "Remove User"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteWorkspaceOpen} onOpenChange={setDeleteWorkspaceOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete workspace: {deleteWorkspaceTarget?.name}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>Choose how to delete this workspace:</p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setDeleteMode("soft")}
                      className={`w-full text-left rounded-md border p-3 text-sm hover-elevate ${
                        deleteMode === "soft" ? "border-primary bg-primary/5" : ""
                      }`}
                      data-testid="option-soft-delete"
                    >
                      <div className="font-medium">Soft delete (recommended)</div>
                      <div className="text-muted-foreground text-xs mt-1">
                        Hides the workspace and disables access. Data is preserved and recoverable.
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteMode("hard")}
                      className={`w-full text-left rounded-md border p-3 text-sm hover-elevate ${
                        deleteMode === "hard" ? "border-destructive bg-destructive/5" : ""
                      }`}
                      data-testid="option-hard-delete"
                    >
                      <div className="font-medium text-destructive">
                        Hard delete (PERMANENT — destroys encryption key)
                      </div>
                      <div className="text-muted-foreground text-xs mt-1">
                        Permanently removes the workspace and ALL its data
                        (clients, leads, invoices, messages, attachments).
                        The workspace's encryption key (KEK) will be
                        scheduled for destruction in the KMS, after which
                        no backup, snapshot, or replica can ever be
                        decrypted. There is no undo. Export anything you
                        need before continuing.
                      </div>
                    </button>
                  </div>
                  {deleteMode === "hard" && (
                    <div className="space-y-2 pt-2">
                      <div
                        className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
                        data-testid="banner-kek-destruction-warning"
                      >
                        <strong>This is unrecoverable.</strong> Confirming
                        will trigger destruction of this workspace's
                        encryption key. Backups, audit-log exports, and
                        archived data tied to this workspace will become
                        permanently unreadable.
                      </div>
                      <label className="text-xs font-medium">
                        Type the workspace name to confirm encryption-key
                        destruction:{" "}
                        <span className="font-mono text-foreground">
                          {deleteWorkspaceTarget?.name}
                        </span>
                      </label>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={deleteWorkspaceTarget?.name || ""}
                        data-testid="input-delete-confirm"
                      />
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete-workspace">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  if (!deleteWorkspaceTarget || !deleteMode) return;
                  if (
                    deleteMode === "hard" &&
                    deleteConfirmText !== deleteWorkspaceTarget.name
                  )
                    return;
                  deleteWorkspaceMutation.mutate({
                    workspaceId: deleteWorkspaceTarget.id,
                    mode: deleteMode,
                    workspaceName: deleteWorkspaceTarget.name,
                  });
                }}
                disabled={
                  !deleteMode ||
                  deleteWorkspaceMutation.isPending ||
                  (deleteMode === "hard" &&
                    deleteConfirmText !== deleteWorkspaceTarget?.name)
                }
                className={
                  deleteMode === "hard"
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : ""
                }
                data-testid="button-confirm-delete-workspace"
              >
                {deleteWorkspaceMutation.isPending
                  ? "Deleting..."
                  : deleteMode === "hard"
                  ? "Permanently delete"
                  : deleteMode === "soft"
                  ? "Soft delete"
                  : "Choose an option"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    {/* ── Credits Management ─────────────────────────────────────────────── */}
    <CreditsDialog
      open={creditsOpen}
      onClose={() => { setCreditsOpen(false); setCreditsWorkspace(null); }}
      workspaceId={creditsWorkspace?.id ?? ""}
      workspaceName={creditsWorkspace?.name ?? ""}
      defaultTab={creditsTab}
    />

    {/* ── Business Phone Provisioning ─────────────────────────────────────── */}
    <PhoneProvisionDialog
      open={phoneDialogOpen}
      workspace={phoneWorkspace}
      onClose={() => { setPhoneDialogOpen(false); setPhoneWorkspace(null); }}
    />
    </>
  );
}

// ── Task #461 — Platform super-admin Event History dialog ─────────────────────
interface EventChangeLogRow {
  id: string;
  workspaceId: string;
  clientId: string;
  invoiceId: string | null;
  fieldName: string;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedByUserId: string;
  changedByName: string | null;
  changedAt: string;
  source: string;
}

const PAGE_SIZE = 50;

const EVENT_FIELD_OPTIONS = [
  { value: "__all", label: "All fields" },
  { value: "booking_created", label: "Booking created" },
  { value: "dateOfEvent", label: "Event Date" },
  { value: "eventTime", label: "Event Time" },
  { value: "eventRate", label: "Event Rate" },
  { value: "discount", label: "Discount" },
  { value: "address", label: "Address" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "eventDetails", label: "Event Details" },
];

function EventHistoryDialog({
  open,
  onOpenChange,
  workspace,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspace: Workspace | null;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fieldFilter, setFieldFilter] = useState("__all");
  const [clientFilter, setClientFilter] = useState("");
  const [debouncedClient, setDebouncedClient] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedClient(clientFilter), 350);
    return () => clearTimeout(t);
  }, [clientFilter]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [debouncedSearch, fieldFilter, debouncedClient]);

  const { data, isLoading } = useQuery<{ rows: EventChangeLogRow[]; total: number }>({
    queryKey: ["/api/platform/workspaces", workspace?.id, "event-change-logs", debouncedSearch, fieldFilter, debouncedClient, page],
    queryFn: async () => {
      if (!workspace?.id) return { rows: [], total: 0 };
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (fieldFilter && fieldFilter !== "__all") params.set("fieldName", fieldFilter);
      if (debouncedClient) params.set("clientId", debouncedClient);
      const res = await fetch(`/api/platform/workspaces/${workspace.id}/event-change-logs?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: open && !!workspace?.id,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (s: string) => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      }).format(new Date(s));
    } catch {
      return s;
    }
  };

  const sourceLabel = (src: string) => {
    if (src === "booking_orchestrate") return "Booking created";
    if (src === "invoice_patch") return "Invoice edit";
    if (src === "client_patch") return "Client edit";
    return src;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="dialog-event-history">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Event History — {workspace?.name ?? ""}
          </DialogTitle>
          <DialogDescription>
            Field-level audit trail for all event/booking changes in this workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 pb-2">
          <Input
            placeholder="Search fields, values, users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-event-history-search"
            className="h-8 text-sm"
          />
          <Select value={fieldFilter} onValueChange={setFieldFilter}>
            <SelectTrigger className="h-8 text-sm" data-testid="select-field-filter">
              <SelectValue placeholder="All fields" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_FIELD_OPTIONS.map((o) => (
                <SelectItem key={o.value || "__all"} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by client ID…"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            data-testid="input-client-id-filter"
            className="h-8 text-sm font-mono"
          />
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center py-12 text-sm text-muted-foreground" data-testid="text-no-event-history">
              {debouncedSearch || (fieldFilter && fieldFilter !== "__all") || debouncedClient
                ? "No matching changes found."
                : "No event changes recorded yet for this workspace."}
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background border-b">
                <tr className="text-left text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">When</th>
                  <th className="pb-2 pr-3 font-medium">Field</th>
                  <th className="pb-2 pr-3 font-medium">From</th>
                  <th className="pb-2 pr-3 font-medium">To</th>
                  <th className="pb-2 pr-3 font-medium">By</th>
                  <th className="pb-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => (
                  <tr key={row.id} data-testid={`row-event-history-${row.id}`} className="hover:bg-muted/30">
                    <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(row.changedAt)}</td>
                    <td className="py-1.5 pr-3 font-medium">{row.fieldLabel ?? row.fieldName}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground max-w-[120px] truncate" title={row.oldValue ?? ""}>
                      {row.oldValue ?? <span className="italic text-muted-foreground/60">—</span>}
                    </td>
                    <td className="py-1.5 pr-3 max-w-[120px] truncate" title={row.newValue ?? ""}>
                      {row.newValue ?? <span className="italic text-muted-foreground/60">—</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{row.changedByName ?? row.changedByUserId.slice(0, 8)}</td>
                    <td className="py-1.5">
                      <Badge variant="secondary" className="text-[10px] font-normal">{sourceLabel(row.source)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
            <span>{total} total {total === 1 ? "change" : "changes"}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                className="h-7 px-2 text-xs"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="button-event-history-prev"
              >
                Prev
              </Button>
              <span>Page {page + 1} of {totalPages}</span>
              <Button
                variant="outline" size="sm"
                className="h-7 px-2 text-xs"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                data-testid="button-event-history-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformWorkspaces() {
  return <PlatformLayout><WorkspacesContent /></PlatformLayout>;
}

interface BillingInfo {
  workspaceId: string;
  workspaceName: string;
  tier: string;
  tierDisplayName: string;
  billingCycle: "monthly" | "annual";
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  priceOverrideMonthly: string | null;
  priceOverrideYearly: string | null;
  subscribedPriceMonthly: string | null;
  subscribedPriceYearly: string | null;
  tierMonthlyPriceCents: number | null;
  tierYearlyPriceCents: number | null;
  resolvedMonthly: { amountDollars: number; source: "override" | "subscribed" | "tier" } | null;
  resolvedYearly: { amountDollars: number; source: "override" | "subscribed" | "tier" } | null;
}

interface BillingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: Workspace | null;
  overrideMonthly: string;
  overrideYearly: string;
  setOverrideMonthly: (v: string) => void;
  setOverrideYearly: (v: string) => void;
}

function BillingDialog({
  open,
  onOpenChange,
  workspace,
  overrideMonthly,
  overrideYearly,
  setOverrideMonthly,
  setOverrideYearly,
}: BillingDialogProps) {
  const { toast } = useToast();

  const { data: billing, isLoading } = useQuery<BillingInfo>({
    queryKey: ["/api/platform/workspaces", workspace?.id, "billing"],
    enabled: !!workspace && open,
  });

  // Hydrate inputs when billing loads
  const hydrationKey = billing?.workspaceId;
  useEffect(() => {
    if (billing) {
      setOverrideMonthly(billing.priceOverrideMonthly ?? "");
      setOverrideYearly(billing.priceOverrideYearly ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationKey]);

  const saveMutation = useMutation({
    mutationFn: async (body: { priceOverrideMonthly: string | null; priceOverrideYearly: string | null }) => {
      return apiRequest("PATCH", `/api/platform/workspaces/${workspace!.id}/pricing`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces", workspace?.id, "billing"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces"] });
      toast({ title: "Pricing updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update", description: e?.message, variant: "destructive" }),
  });

  const sourceLabel = (s: "override" | "subscribed" | "tier" | undefined) => {
    if (s === "override") return <Badge variant="default" className="bg-amber-500">Override</Badge>;
    if (s === "subscribed") return <Badge variant="secondary">Locked-in</Badge>;
    if (s === "tier") return <Badge variant="outline">Tier price</Badge>;
    return null;
  };

  const fmt = (cents: number | null | undefined) =>
    cents === null || cents === undefined ? "—" : `$${(cents / 100).toFixed(2)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="dialog-billing">
        <DialogHeader>
          <DialogTitle>Billing — {workspace?.name}</DialogTitle>
          <DialogDescription>
            Set manual price overrides. Override → Locked-in price → Tier default (lower wins is highest).
          </DialogDescription>
        </DialogHeader>

        {isLoading || !billing ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading billing…</div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-md border p-3 text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Current tier</span>
                <Badge variant="secondary">{billing.tierDisplayName}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Billing cycle</span>
                <span>{billing.billingCycle === "annual" ? "Annual" : "Monthly"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Stripe subscription</span>
                <span className="font-mono text-xs">{billing.stripeSubscriptionId || "Not subscribed"}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground mb-1">Effective monthly</div>
                <div className="text-2xl font-semibold" data-testid="text-effective-monthly">
                  {billing.resolvedMonthly ? `$${billing.resolvedMonthly.amountDollars.toFixed(2)}` : "—"}
                </div>
                <div className="mt-1">{sourceLabel(billing.resolvedMonthly?.source)}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground mb-1">Effective yearly</div>
                <div className="text-2xl font-semibold" data-testid="text-effective-yearly">
                  {billing.resolvedYearly ? `$${billing.resolvedYearly.amountDollars.toFixed(2)}` : "—"}
                </div>
                <div className="mt-1">{sourceLabel(billing.resolvedYearly?.source)}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="override-monthly">Override monthly ($)</Label>
                <Input
                  id="override-monthly"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={`Tier: ${fmt(billing.tierMonthlyPriceCents)}${
                    billing.subscribedPriceMonthly ? ` · Locked-in: $${parseFloat(billing.subscribedPriceMonthly).toFixed(2)}` : ""
                  }`}
                  value={overrideMonthly}
                  onChange={(e) => setOverrideMonthly(e.target.value)}
                  data-testid="input-override-monthly"
                />
              </div>
              <div>
                <Label htmlFor="override-yearly">Override yearly ($)</Label>
                <Input
                  id="override-yearly"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={`Tier: ${fmt(billing.tierYearlyPriceCents)}${
                    billing.subscribedPriceYearly ? ` · Locked-in: $${parseFloat(billing.subscribedPriceYearly).toFixed(2)}` : ""
                  }`}
                  value={overrideYearly}
                  onChange={(e) => setOverrideYearly(e.target.value)}
                  data-testid="input-override-yearly"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank to remove override and fall back to locked-in or tier price. Override only affects future invoices and upgrade-flow prices — it does not retroactively change Stripe subscriptions.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOverrideMonthly("");
              setOverrideYearly("");
              saveMutation.mutate({ priceOverrideMonthly: null, priceOverrideYearly: null });
            }}
            disabled={saveMutation.isPending || isLoading}
            data-testid="button-clear-overrides"
          >
            Clear overrides
          </Button>
          <Button
            onClick={() =>
              saveMutation.mutate({
                priceOverrideMonthly: overrideMonthly === "" ? null : overrideMonthly,
                priceOverrideYearly: overrideYearly === "" ? null : overrideYearly,
              })
            }
            disabled={saveMutation.isPending || isLoading}
            data-testid="button-save-overrides"
          >
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Business Phone Provisioning Dialog ────────────────────────────────────────
function PhoneProvisionDialog({
  open,
  workspace,
  onClose,
}: {
  open: boolean;
  workspace: { id: string; name: string; slug: string } | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [areaCode, setAreaCode] = useState("");

  const phoneQuery = useQuery<{
    provisioned: boolean;
    status?: string;
    phoneNumber?: string;
    areaCode?: string;
    subaccountSid?: string;
    voiceAppSid?: string;
    apiKeySid?: string;
    provisionedAt?: string;
  }>({
    queryKey: ["/api/platform/workspaces", workspace?.id, "phone"],
    queryFn: async () => {
      if (!workspace?.id) return { provisioned: false };
      const res = await fetch(`/api/platform/workspaces/${workspace.id}/phone`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load phone config");
      return res.json();
    },
    enabled: open && !!workspace?.id,
  });

  const provision = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/platform/workspaces/${workspace!.id}/phone/provision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ areaCode: areaCode || "800" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Provisioning failed");
      return data;
    },
    onSuccess: (data) => {
      toast({ title: "Phone provisioned", description: data.phoneNumber });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces", workspace?.id, "phone"] });
    },
    onError: (e: any) => toast({ title: "Provisioning failed", description: e?.message, variant: "destructive" }),
  });

  const deprovision = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/platform/workspaces/${workspace!.id}/phone`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Deprovisioning failed");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Phone deprovisioned", description: "Twilio subaccount closed." });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/workspaces", workspace?.id, "phone"] });
    },
    onError: (e: any) => toast({ title: "Deprovision failed", description: e?.message, variant: "destructive" }),
  });

  const cfg = phoneQuery.data;
  const isActive = cfg?.provisioned && cfg.status === "active";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Business Phone — {workspace?.name}
          </DialogTitle>
          <DialogDescription>
            Clarik provisions a dedicated Twilio subaccount for this workspace. The tenant never sees Twilio credentials.
          </DialogDescription>
        </DialogHeader>

        {phoneQuery.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : isActive ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-muted/30 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Phone number</span>
                <span className="font-mono text-sm font-semibold" data-testid="text-provisioned-number">{cfg?.phoneNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                <Badge variant="default" className="bg-green-600 hover:bg-green-600" data-testid="badge-phone-status">Active</Badge>
              </div>
              {cfg?.subaccountSid && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Subaccount</span>
                  <span className="font-mono text-xs text-muted-foreground">{cfg.subaccountSid.slice(0, 20)}…</span>
                </div>
              )}
              {cfg?.provisionedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Provisioned</span>
                  <span className="text-xs text-muted-foreground">{new Date(cfg.provisionedAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-3 text-xs text-amber-800 dark:text-amber-200">
              Deprovisioning closes the Twilio subaccount. The number is released back to Twilio's pool and cannot be recovered.
            </div>
          </div>
        ) : cfg?.provisioned && cfg.status === "deprovisioned" ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-muted/30 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Previous number</span>
                <span className="font-mono text-sm text-muted-foreground">{cfg?.phoneNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                <Badge variant="secondary" data-testid="badge-phone-status">Deprovisioned</Badge>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No phone number provisioned. Enter a preferred US area code (e.g. 312 for Chicago) or leave blank for any available number.
            </p>
            <div>
              <Label htmlFor="phone-area-code">Area code (optional)</Label>
              <Input
                id="phone-area-code"
                placeholder="e.g. 312"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                maxLength={3}
                className="mt-1"
                data-testid="input-phone-area-code"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="button-phone-cancel">Close</Button>
          {isActive ? (
            <Button
              variant="destructive"
              onClick={() => deprovision.mutate()}
              disabled={deprovision.isPending}
              data-testid="button-phone-deprovision"
            >
              {deprovision.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Deprovision
            </Button>
          ) : (
            <Button
              onClick={() => provision.mutate()}
              disabled={provision.isPending || phoneQuery.isLoading}
              data-testid="button-phone-provision"
            >
              {provision.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {provision.isPending ? "Provisioning…" : "Provision Phone"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
